import { Auth, Cache } from "aws-amplify";
import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { SAButton, SAHeader, SASpinner, SAUX360Theme } from '@saux/design-system-react';
import env from '../../env_config';
import { clearApplicationSession, logoutPendingKey } from '../../utils/logout';

type LogoutProps = {
  complete?: boolean;
};

const LogoutGlobalStyle = createGlobalStyle`
  body {
    margin: 0;
  }
`;

const Header = styled(SAHeader)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 20px;
  font-weight: 700;
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.color.background.background};
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Lato', sans-serif;

  &, & * {
    box-sizing: border-box;
  }
`;

const Content = styled.main`
  display: flex;
  justify-content: center;
  padding: 64px 24px;

  @media (max-width: 600px) {
    padding: 32px 16px;
  }
`;

const Panel = styled.section`
  width: 100%;
  max-width: 540px;
  padding: 40px 32px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadow.elevation.level2};
  text-align: center;

  h1 {
    margin: 24px 0 16px;
    font-size: 26px;
    line-height: 1.3;
  }

  p {
    margin: 0 0 20px;
    line-height: 1.6;
  }

  @media (max-width: 600px) {
    padding: 32px 20px;
  }
`;

const Notice = styled.p`
  padding: 16px;
  border-left: 4px solid ${({ theme }) => theme.colors.info};
  background: ${({ theme }) => theme.color.background.background};
  text-align: left;
`;

export default function Logout({ complete = false }: LogoutProps) {
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    async function signout() {
      setError(false);
      try {
        if (complete) {
          clearApplicationSession();
          Cache.clear();
          sessionStorage.removeItem(logoutPendingKey);
          window.location.replace(env.saconnect);
          return;
        }

        sessionStorage.setItem(logoutPendingKey, 'true');
        clearApplicationSession();
        Cache.clear();
        await Auth.signOut({ global: true });

        // Amplify may resolve without navigation when no Hosted UI user remains.
        const logoutUrl = new URL(`https://${env.oauth.domain}/logout`);
        logoutUrl.searchParams.set('client_id', env.authentication.aws_user_pools_web_client_id);
        logoutUrl.searchParams.set('logout_uri', env.oauth.redirectSignOut);
        window.location.assign(logoutUrl.toString());
      } catch (err) {
        console.error('Self-service sign out failed', err);
        sessionStorage.removeItem(logoutPendingKey);
        setError(true);
      }
    }

    signout();
  }, [complete, attempt]);

  return (
    <ThemeProvider theme={SAUX360Theme}>
      <LogoutGlobalStyle />
      <Page>
        <Header><span>Self-Service</span></Header>
        <Content>
          <Panel aria-labelledby="logout-title" aria-busy={!error}>
            {error ? (
              <>
                <div role="alert">
                  <h1 id="logout-title">{complete ? 'Unable to return to Connect' : 'Unable to complete sign out'}</h1>
                  <p>{complete
                    ? 'We could not finish returning you to SAConnect after sign out.'
                    : 'We could not confirm that sign out completed. Please check your connection and try again.'}</p>
                </div>
                <Notice>
                  {complete
                    ? 'Retry to continue to SAConnect. If the problem persists, contact Customer Service.'
                    : 'Your local Self-Service data may already have been cleared. If you are using a shared device, do not leave your browser unattended.'}
                </Notice>
                <SAButton
                  label={complete ? 'Retry Connect' : 'Retry sign out'}
                  variant="primary-medium"
                  onClick={() => setAttempt(value => value + 1)}
                />
              </>
            ) : (
              <div role="status" aria-live="polite">
                <div aria-hidden="true">
                  <SASpinner variant="circular-continuous" size="48px" color={SAUX360Theme.colors.primary} delay={0} />
                </div>
                <h1 id="logout-title">{complete ? 'Returning to Connect...' : 'Signing out...'}</h1>
                <p>{complete
                  ? 'Finishing sign out and taking you back to SAConnect. Please keep this window open.'
                  : 'Please wait while we sign you out of Self-Service. You will be redirected to SAConnect when this is complete.'}</p>
              </div>
            )}
          </Panel>
        </Content>
      </Page>
    </ThemeProvider>
  );
}
